// components/DegreeAudit/Common/DebugSection.tsx

import { memo } from 'react';

interface RequirementNodeShape {
  name: string;
  children?: RequirementNodeShape[];
  [key: string]: unknown;
}

interface DebugMajor {
  module?: string;
  requirements?: RequirementNodeShape[];
  credits_completed?: number;
  credits_in_progress?: number;
  credits_planned?: number;
  credits_available?: number;
  credits_required?: number;
  [key: string]: unknown;
}

export interface HierarchicalDataDebugShape {
  majors?: DebugMajor[];
  credits_completed?: number;
  credits_in_progress?: number;
  credits_planned?: number;
  credits_available?: number;
  on_track_to_graduate?: boolean;
  overall_progress?: number;
  [key: string]: unknown;
}

export interface DebugSectionProps {
  hierarchicalData?: HierarchicalDataDebugShape | null;
  academicPlan?: { id?: number | string } | null;
  chatDebugInfo?: unknown;
}

const DebugSection = memo(({ hierarchicalData, academicPlan }: DebugSectionProps) => {
  const getHierarchicalRequirementNamesRecursive = (
    nodes: RequirementNodeShape[],
    names: string[] = []
  ): string[] => {
    nodes.forEach((node) => {
      names.push(node.name);
      if (node.children && node.children.length > 0) {
        getHierarchicalRequirementNamesRecursive(node.children, names);
      }
    });
    return names;
  };

  const getHierarchicalRequirementNames = (
    majors: DebugMajor[] | undefined,
    names: string[] = []
  ): string[] => {
    if (!majors) return names;

    majors.forEach((major) => {
      if (major.requirements) {
        major.requirements.forEach((req) => {
          names.push(req.name);
          if (req.children && req.children.length > 0) {
            getHierarchicalRequirementNamesRecursive(req.children, names);
          }
        });
      }
    });
    return names;
  };

  const hierarchicalNames = hierarchicalData?.majors
    ? getHierarchicalRequirementNames(hierarchicalData.majors)
    : [];

  return (
    <div className="mt-6 md:mt-8 border-t pt-4 md:pt-6 hidden md:block print-hide">
      <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-800">
        Debug Information
      </h3>

      <div className="mb-4 md:mb-6 bg-green-50 p-3 md:p-4 rounded">
        <h4 className="font-medium text-gray-700 mb-2 text-sm md:text-base">
          OVERALL Credit Breakdown (All Majors Combined):
        </h4>
        <div className="text-xs md:text-sm space-y-1">
          <div>Completed: {hierarchicalData?.credits_completed || 0} credits</div>
          <div>In Progress: {hierarchicalData?.credits_in_progress || 0} credits</div>
          <div>Planned: {hierarchicalData?.credits_planned || 0} credits</div>
          <div>Available: {hierarchicalData?.credits_available || 0} credits</div>
          <div>On Track: {hierarchicalData?.on_track_to_graduate ? 'Yes' : 'No'}</div>
        </div>
      </div>

      {hierarchicalData?.majors && hierarchicalData.majors.length > 0 && (
        <div className="mb-4 md:mb-6 bg-blue-50 p-3 md:p-4 rounded">
          <h4 className="font-medium text-gray-700 mb-2 text-sm md:text-base">
            PER-MAJOR Credit Breakdowns:
          </h4>
          <div className="text-xs md:text-sm space-y-2">
            {hierarchicalData.majors.map((major, index) => (
              <div key={index} className="border-l-2 border-blue-300 pl-2">
                <div className="font-medium">{major.module}:</div>
                <div className="ml-2 space-y-0.5">
                  <div>Completed: {major.credits_completed || 0} credits</div>
                  <div>In Progress: {major.credits_in_progress || 0} credits</div>
                  <div>Planned: {major.credits_planned || 0} credits</div>
                  <div>Available: {major.credits_available || 0} credits</div>
                  <div>
                    Total:{' '}
                    {(major.credits_completed || 0) +
                      (major.credits_in_progress || 0) +
                      (major.credits_planned || 0) +
                      (major.credits_available || 0)}
                    /{major.credits_required || 120}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-gray-100 p-3 md:p-4 rounded">
        <h4 className="font-medium mb-2 text-sm md:text-base">Summary:</h4>
        <ul className="text-xs md:text-sm space-y-1">
          <li>• Hierarchical nodes: {hierarchicalNames.length}</li>
          <li>• Academic Plan ID: {academicPlan?.id || 'N/A'}</li>
          <li>• Majors: {hierarchicalData?.majors?.length || 0}</li>
          <li>• Credits Completed: {hierarchicalData?.credits_completed || 0}</li>
          <li>• Credits In Progress: {hierarchicalData?.credits_in_progress || 0}</li>
          <li>• Credits Planned: {hierarchicalData?.credits_planned || 0}</li>
          <li>
            • Overall Progress: {Math.round((hierarchicalData?.overall_progress || 0) * 10) / 10}%
          </li>
          <li>• On Track: {hierarchicalData?.on_track_to_graduate ? 'Yes' : 'No'}</li>
        </ul>
      </div>
    </div>
  );
});

DebugSection.displayName = 'DebugSection';

export default DebugSection;
